/**
 * Statsig wrapper module
 *
 * Provides:
 * - A drop-in replacement for `import Statsig from 'statsig-js'` via the
 *   `Statsig` Proxy that lazily forwards to our @statsig/js-client instance
 *   (which is bootstrapped from server values, see clientConfig.ts).
 * - `useExperiment` / `useGate` hooks.
 * - Pre-existing session-storage helpers used by useStatsigExperiment.
 *
 * Usage:
 *   import { Statsig } from '@/utils/statsig'
 *   Statsig?.getExperiment('experiment_name')?.get('variant', 'control')
 */

import { useMemo } from 'react'
import type { StatsigClient } from '@statsig/js-client'

import {
    getStatsigClient,
    isStatsigClientInitialized
} from '@/analytics/statsig/clientConfig'

// ---------------------------------------------------------------------------
// Existing helpers (kept for back-compat)
// ---------------------------------------------------------------------------

export const setFailedExperimentInSessionStorage = (experimentName: string) => {
    let failedExperimentArray = getFailedExperimentsFromSessionStorage()
    failedExperimentArray.push(experimentName)
    sessionStorage.setItem('failedExperimentArray', JSON.stringify(failedExperimentArray))
}

export const getFailedExperimentsFromSessionStorage = () => {
    try {
        const data = sessionStorage.getItem('failedExperimentArray') || ''
        return data ? JSON.parse(data) : []
    } catch (e) {
        return []
    }
}

export const checkIfExperimentFailedOnPrevPage = (experimentName: string) => {
    let failedExperimentArray = getFailedExperimentsFromSessionStorage()
    if (failedExperimentArray?.includes(experimentName)) {
        return true
    }
    return false
}

// ---------------------------------------------------------------------------
// Drop-in Statsig client wrapper (server-bootstrapped)
// ---------------------------------------------------------------------------

// Proxy that lazily gets the Statsig client on each access
// This ensures the client is fetched at call time (after initialization)
// rather than at module load time (before initialization)
export const Statsig = new Proxy({} as StatsigClient, {
    get(_, prop: string | symbol) {
        const client = getStatsigClient()
        if (!client) return undefined

        const value = client[prop as keyof StatsigClient]
        // Bind functions to the client instance
        return typeof value === 'function' ? value.bind(client) : value
    }
})

/**
 * Hook replacement for statsig-react's useExperiment.
 *
 * Usage:
 *   const { config, isLoading } = useExperiment('experiment_name')
 *   const variant = config?.get('variant', 'control')
 */
export const useExperiment = (experimentName: string) => {
    const experiment = useMemo(() => {
        const client = getStatsigClient()
        if (!client) {
            return {
                config: { value: {} as Record<string, unknown> },
                isLoading: true
            }
        }

        const exp = client.getExperiment(experimentName)
        return {
            config: exp,
            isLoading: false
        }
    }, [experimentName])

    return experiment
}

/**
 * Hook replacement for statsig-react's useGate.
 */
export const useGate = (gateName: string) => {
    const gate = useMemo(() => {
        const client = getStatsigClient()
        if (!client) {
            return {
                value: false,
                isLoading: true
            }
        }

        return {
            value: client.checkGate(gateName),
            isLoading: false
        }
    }, [gateName])

    return gate
}

// Re-export client config helpers
export {
    getStatsigClient,
    getClientExperiment,
    checkClientGate,
    isStatsigClientInitialized
} from '@/analytics/statsig/clientConfig'

// Default export so call sites can do `import Statsig from '@/utils/statsig'`
// (drop-in for the legacy `import Statsig from 'statsig-js'`)
export default Statsig
